import { defineStore } from 'pinia';
import { CreatorInfo, getCreatorInfo, prettyWallet } from '@/api/creator';
import {
  getOAuth,
  getSubscriber,
  resendVerificationEmail,
  subscribe,
  unsubscribeFromCreator
} from '@/api/subscriptions';
import { States } from '@/common/constants';

interface State {
  state: string;
  email: string;
  error: string;
  creatorInfo: CreatorInfo | undefined;
  loading: boolean;
  showPromptSubscribe: boolean;
  isModalOpen: boolean;
  verificationEmailSent: boolean;
  subscriberWallet: string | undefined;
}

export const useSubscribeStore = defineStore('subscribe', {
  state: (): State => {
    return {
      state: States.NotConnected,
      email: '',
      error: '',
      creatorInfo: undefined,
      loading: false,
      showPromptSubscribe: false,
      isModalOpen: false,
      verificationEmailSent: false,
      subscriberWallet: undefined
    };
  },
  getters: {
    ensOrWallet: (state) =>
      state.creatorInfo?.ens
        ? state.creatorInfo.ens
        : prettyWallet(state.creatorInfo?.walletAddress ?? '')
  },
  actions: {
    handleAddressChanged(): void {
      this.email = '';
      this.subscriberWallet = window.ManifoldEthereumProvider.selectedAddress();

      if (!this.subscriberWallet) {
        this.state = States.NotConnected;
        return;
      }

      // Check if user is authenticated
      if (!window.manifold?.isAuthenticated) {
        this.state = States.NotAuthenticated;
        return;
      }
    },
    async handleAddressAuthenticated() {
      this.loading = true;

      if (!this.creatorInfo?.walletAddress || !this.subscriberWallet) {
        this.loading = false;
        return;
      }

      try {
        // Check if user is subscribed
        const subscriber = await getSubscriber(this.subscriberWallet);

        if (!subscriber) {
          // Not subscribed, no email linked
          this.state = States.NotSubscribedNoEmail;
          this.loading = false;
          return;
        }

        this.email = subscriber.email;

        // Find subscription
        const subscription = subscriber.subscriptions.find(
          (sub) => !!sub && sub.creatorAddress === this.creatorInfo?.walletAddress.toLowerCase()
        );

        if (!subscription) {
          // Not subscribed, email linked
          this.state = States.NotSubscribedEmailLinked;

          if (this.isModalOpen) {
            this.isModalOpen = false;
            await this.subscribe();
          }

          this.loading = false;
          return;
        }

        // Check if email is verified
        if (!subscriber.verified) {
          // Subscribed, email not verified
          this.state = States.SubscribedNotVerified;
          this.loading = false;
          return;
        }

        // Subscribed and email verified
        this.state = States.Subscribed;
        this.isModalOpen = false;
        this.loading = false;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        this.error = e.message;
      }
    },
    handleAddressUnauthenticated() {
      this.error = '';
      this.loading = false;
      this.verificationEmailSent = false;

      this.subscriberWallet = window.ManifoldEthereumProvider.selectedAddress();

      if (!this.subscriberWallet) {
        this.state = States.NotConnected;
        return;
      }

      this.state = States.NotAuthenticated;
    },
    async subscribe() {
      if (!this.creatorInfo?.walletAddress || !this.subscriberWallet) {
        return;
      }

      if (!this.email) {
        this.error = 'Please enter an email';
        return;
      }

      try {
        this.loading = true;
        await subscribe(this.creatorInfo.walletAddress, this.email, this.subscriberWallet);

        // Check if user is verified
        const subscriber = await getSubscriber(this.subscriberWallet);

        // Check if email is verified
        if (!subscriber?.verified) {
          // Subscribed, email not verified
          this.state = States.SubscribedNotVerified;
          this.loading = false;
          this.isModalOpen = true;
          return;
        }

        // Subscribed and email verified
        this.state = States.Subscribed;
        this.isModalOpen = false;
        this.loading = false;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        this.loading = false;
        this.error = e.message;
      }
    },
    async resendVerificationEmail() {
      this.verificationEmailSent = false;

      if (!this.subscriberWallet) {
        return;
      }

      try {
        this.loading = true;
        await resendVerificationEmail(this.subscriberWallet);
        this.loading = false;
        this.verificationEmailSent = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        this.loading = false;
        this.error = e.message;
      }
    },
    async unsubscribe() {
      if (!this.creatorInfo?.walletAddress || !this.subscriberWallet) {
        return;
      }

      this.error = '';
      this.loading = true;

      try {
        await unsubscribeFromCreator(this.creatorInfo.walletAddress, this.subscriberWallet);
        this.isModalOpen = false;
        this.state = States.NotSubscribedEmailLinked;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        this.error = e.message;
      }

      this.loading = false;
    },
    async authenticateWallet(): Promise<void> {
      await getOAuth();
      window.dispatchEvent(new CustomEvent('m-reauthenticate'));
    },
    async verifyWallet(): Promise<void> {
      this.loading = true;

      try {
        await this.authenticateWallet();
      } catch (e) {
        this.loading = false;
        return;
      }

      // Loading keeps being true until handleAuthenticated says the contrary
      this.loading = true;
      return;
    },
    handleUnsubscribe(): void {
      this.showPromptSubscribe = false;
      this.isModalOpen = true;
    },
    async handleSubscribe(): Promise<void> {
      this.showPromptSubscribe = false;

      if (this.state === States.NotSubscribedEmailLinked) {
        await this.subscribe();
        return;
      }

      this.isModalOpen = true;
    },
    async fetchCreatorInfo(creatorAddress: string): Promise<void> {
      this.creatorInfo = {
        walletAddress: creatorAddress
      };
      this.creatorInfo = await getCreatorInfo(creatorAddress);
    },
    closeModal(): void {
      this.isModalOpen = false;
    },
    openModal(): void {
      this.isModalOpen = true;
    },
    promptSubscribe(): void {
      if (this.state === States.Subscribed || this.state === States.NotConnected) {
        return;
      }

      this.showPromptSubscribe = true;
      this.isModalOpen = true;
    }
  }
});
